import React from 'react';
import cx from 'classnames';
import { constants } from '@trucktrax/trucktrax-common';
import { ProductLine } from '@trucktrax/trucktrax-ts-common';
import styles from '../../admin/plants/PlantsAddModal.module.css';
import { GEOZONE_TYPE, ICON_ARROW_CLASS, ICON_DOT_CLASS } from '../../../constants/appConstants';
import { ZoneType } from '../../../types';
import { productlineLanguageReturnGeozone } from '../../../util/adminUtil';

export interface GeozoneWarningTextProps {
  zoneType: ZoneType;
  /** Optional on purpose: a legacy plant can carry no product line (`PlantDto.productLine?`). */
  productLine?: ProductLine | '';
  className?: string;
}

/**
 * The red "Warning: ..." sentence naming the status a plant geozone sets, shared by the Add Plant
 * wizard's geozone steps and the plant detail view's geozone editor so the copy lives in one place.
 *
 * Keep this a plain, hook-free function component: the copy tests expand it by calling it with its
 * props, outside of a renderer.
 *
 * `geozone-warning` is a global marker class so page styles that target `p:first-of-type` (the
 * plant detail view's edit wrapper) can exempt this paragraph.
 */
export default function GeozoneWarningText({
  zoneType,
  productLine,
  className,
}: GeozoneWarningTextProps) {
  const paragraphClassName = cx(styles.warningText, 'geozone-warning', className);

  if (zoneType === GEOZONE_TYPE.PLANT) {
    return (
      <p className={paragraphClassName}>
        <span className={cx('txt-bold')}>Warning</span>: When a vehicle exits this geozone, it will
        set the driver&#39;s ticket status to&nbsp;
        <span className={cx('txt-bold')}>
          {productLine === ProductLine.Cement
            ? constants.STATUS_NAMES.ToPlant
            : constants.STATUS_NAMES.ToJob}
        </span>
        &nbsp;&#40;
        <i
          className={cx(
            ICON_ARROW_CLASS,
            styles.inlineStatusIcon,
            styles.statusIconArrow,
            styles.statusIconToJob,
          )}
        />
        &#41;.
      </p>
    );
  }

  if (zoneType === GEOZONE_TYPE.INQUEUE) {
    return (
      <p className={paragraphClassName}>
        <span className={cx('txt-bold')}>Warning</span>: When a vehicle enters this geozone without
        a ticket, it will set the vehicle status to&nbsp;
        <span className={cx('txt-bold')}>
          {productLine === ProductLine.ReadyMix
            ? constants.STATUS_NAMES.ReadyToLoad
            : constants.STATUS_NAMES.InQueue}
        </span>
        &nbsp;&#40;
        <i
          className={cx(
            ICON_DOT_CLASS,
            styles.inlineStatusIcon,
            styles.statusIconDot,
            styles.statusIconInQueue,
          )}
        />
        &#41;.
      </p>
    );
  }

  if (zoneType !== GEOZONE_TYPE.RETURN) {
    return null;
  }

  const returnStatusName = productLine ? productlineLanguageReturnGeozone(productLine) : '';

  // A plant without a product line has no Return status name; hide the warning rather than
  // render "...status to  ( )."
  if (!returnStatusName) {
    return null;
  }

  return (
    <p className={paragraphClassName}>
      <span className={cx('txt-bold')}>Warning</span>: When a vehicle enters this geozone, it will
      set the driver&#39;s ticket status to&nbsp;
      <span className={cx('txt-bold')}>{returnStatusName}</span>
      &nbsp;&#40;
      <i
        className={cx(
          ICON_DOT_CLASS,
          styles.inlineStatusIcon,
          styles.statusIconDot,
          styles.statusIconReturn,
        )}
      />
      &#41;.
    </p>
  );
}
